import { useJobTypeForms } from "./useJobTypeForms";
import "./FormSelector.css";

interface FormSelectorProps {
  jobTypeId: string;
  /** The currently active form id. null = no form selected (handled gracefully). */
  activeFormId: string | null;
  /** Called when the user clicks a form chip. Always passes a concrete form id. */
  onSelectForm: (formId: string) => void;
  /** Delegate: parent opens AddFormDialog (Task 4). */
  onAddForm: () => void;
  /** Delegate: parent opens ManageFormsDialog (Task 5). */
  onManageForms: () => void;
}

/**
 * FormSelector — page-level form chip row that sits ABOVE the Form Config
 * tab bar (mounted in Task 3).
 *
 * Key differences from the in-pane FormStrip:
 *  - One chip per form, including the default form (no special null/"Default scope" chip).
 *  - The default form chip shows a ★ marker.
 *  - Add and Manage are delegated to parent callbacks; nothing is opened inline.
 *  - No gear button, no inline rename, no ManageFormsDialog mount here.
 *  - activeFormId is always a concrete form id at runtime; null is handled
 *    gracefully (no chip is active).
 */
export function FormSelector({
  jobTypeId,
  activeFormId,
  onSelectForm,
  onAddForm,
  onManageForms,
}: FormSelectorProps) {
  const { forms, loading } = useJobTypeForms(jobTypeId);

  if (loading) {
    return (
      <div className="form-selector form-selector--loading">Loading forms…</div>
    );
  }

  return (
    <div className="form-selector" data-testid="form-selector">
      {forms.map((form) => {
        const isActive = activeFormId === form.id;
        // Default form chip includes ★ in visible text and "(default)" in
        // aria-label so screen readers and test queries can identify it.
        const ariaLabel = form.isDefault ? `${form.name} (default)` : form.name;
        const visibleLabel = form.isDefault ? `${form.name} ★` : form.name;
        return (
          <button
            key={form.id}
            type="button"
            className={`form-selector__chip${isActive ? " form-selector__chip--active" : ""}`}
            onClick={() => onSelectForm(form.id)}
            aria-label={ariaLabel}
            aria-pressed={isActive}
            data-active={isActive ? "true" : "false"}
            data-form-id={form.id}
          >
            {visibleLabel}
          </button>
        );
      })}

      {/* + Add Form — delegates to parent; no inline state here */}
      <button
        type="button"
        className="form-selector__add-btn"
        onClick={onAddForm}
        aria-label="+ Add Form"
      >
        + Add Form
      </button>

      {/* Manage forms — pushed to far right via margin-left: auto */}
      <button
        type="button"
        className="form-selector__manage-btn"
        onClick={onManageForms}
        aria-label="Manage forms"
      >
        Manage forms
      </button>
    </div>
  );
}
